import React from 'react';
import classNames from 'classnames';

import type {FileInfo} from '../../../utils/attachments/types';

import {DownloadIcon} from '../../../icons/download';
import {TypographySize, useTypography} from '../../../hooks/typography';

import {fileSizeToString} from '../../../utils/attachments/utils';

import {FileThumbnail} from './file-thumbnail';
import {FilenameOverlay} from './filename-overlay';

import styles from './styles.module.css';

type Props = {
    fileInfo: FileInfo;
    interactive?: boolean;
    handleAttachmentClick?: (fileInfo: FileInfo) => void;
    compactDisplay?: boolean;
    ariaLabelText: string;
    canDownloadFiles?: boolean;
    enableSVGs?: boolean;
    renderMenuItems?: (info: FileInfo) => React.ReactNode;
    getFileUrl: (id: string) => string;
    getThumbnailUrl: (id: string) => string;
    getDownloadUrl: (id: string) => string;
    onDownload?: () => void;
    tooltipText: string;
    className?: string;
};

export const FileAttachment = ({
    ariaLabelText,
    compactDisplay,
    handleAttachmentClick,
    fileInfo,
    canDownloadFiles,
    className,
    renderMenuItems,
    enableSVGs,
    getDownloadUrl,
    tooltipText,
    getFileUrl,
    getThumbnailUrl,
    interactive,
    onDownload,
}: Props) => {
    let fileThumbnail = null;
    let fileDetail = null;
    let fileActions = null;

    const onAttachmentClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        e.preventDefault();
        e.stopPropagation();

        if ('blur' in e.target) {
            (e.target as HTMLElement).blur();
        }

        if (interactive) {
            handleAttachmentClick?.(fileInfo);
        }
    };

    if (!compactDisplay) {
        fileThumbnail = (
            <div aria-label={ariaLabelText} className={styles.fileThumbnailWrapper}>
                <FileThumbnail
                    fileInfo={fileInfo}
                    enableSVGs={enableSVGs}
                    getFileUrl={getFileUrl}
                    getThumbnailUrl={getThumbnailUrl}
                />
            </div>
        );

        fileDetail = (
            <div className={styles.details}>
                <span className={styles.detailsName}>{fileInfo.name}</span>
                <span className={styles.detailsTypeSize}>
                    {`${fileInfo.extension.toUpperCase()} ${fileSizeToString(fileInfo.size)}`}
                </span>
            </div>
        );

        fileActions = renderMenuItems?.(fileInfo);
    }

    let filenameOverlay;

    if (canDownloadFiles) {
        filenameOverlay = (
            <FilenameOverlay
                fileInfo={fileInfo}
                compactDisplay={compactDisplay}
                getDownloadUrl={getDownloadUrl}
                onClick={onDownload}
                tooltipText={tooltipText}
            >
                <DownloadIcon width={18} height={18} />
            </FilenameOverlay>
        );
    }

    const typographyCls = useTypography({
        size: TypographySize.BodyS,
        noDefaultColor: true,
    });

    return (
        <div
            onClick={onAttachmentClick}
            className={classNames({
                className,
                [styles.fileAttachment]: !compactDisplay,
                [styles.fileAttachmentCompact]: compactDisplay,
                [styles.interactive]: interactive,
            })}
        >
            {fileThumbnail}
            <div className={classNames(styles.info, typographyCls)}>
                {fileDetail}
                {fileActions}
                {filenameOverlay}
            </div>
        </div>
    );
};
